import React, { useState, useCallback, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useHistory, useParams, Link } from "react-router-dom";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Container from "../../../../components/Container";
import Layout from "../../../../components/Layout";
import Content from "../../../../components/Content";
import Flex from "../../../../components/Flex";
import Label from "../../../../components/Label";
import TextBox from "../../../../components/TextBox";
import Button from "../../../../components/Button";
import ButtonList from "../../../../components/ButtonList";
import Tooltip from "../../../../components/Tooltip";
import CheckBox from "../../../../components/CheckBox";
import { useFetch } from "../../../../components/useFetch";
import { useMessageToast } from "../../../../components/MessageProvider";
import { useAuthState } from "../../../../components/AuthProvider";
import errorMessageHandler from "../../errorMessageHandler";
import Spinner from "../../../../components/Loader/Spinner";

import styles from "./AddOrganization.module.scss";

const AddOrganization = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const orgApiUrl = `${clientConfigApiDomain}/organizations`;
  const [useMrnAsSourceEntityIdMode, setUseMrnAsSourceEntityIdMode] = useState(
    false
  );

  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    {
      data: addEditUserData,
      hasLoaded: addEditHasLoaded,
      hasError: addEditHasError,
      error
    },
    addEditFetch
  ] = useFetch();

  const [orgData, setOrgData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loginType, setLoginType] = useState("MS-Email");
  const [shortCodesList, setShortCodesList] = useState([]);

  const fetchData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    const response = await fetch(`${orgApiUrl}/${id}/`);
    const data = await response.json();
    setOrgData(data);
    setIsLoading(false);
    setHasLoaded(true);
    setUseMrnAsSourceEntityIdMode(data.use_mrn_as_source_entity_id);
  }, [id, orgApiUrl]);

  useEffect(() => {
    if (!hasLoaded && id !== undefined) {
      fetchData();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
      if (!id) {
        setUseMrnAsSourceEntityIdMode(true);
      }
    }
  }, [id, setIsLoading, hasLoaded, fetchData]);

  useEffect(() => {
    if (id === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Organization added successfully.`);

        history.push(`/admin/organizations/${addEditUserData.id}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to add organization");
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Organization updated successfully.`);

        history.push(`/admin/organizations/${addEditUserData.id}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to update organization");
      }
    }
  }, [
    id,
    addEditUserData,
    addEditHasLoaded,
    addEditHasError,
    toast,
    history,
    error
  ]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        {id ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/organizations/${id}`}>
                  Organization: {orgData.name}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item
            last
            render={() => "Add Organization"}
          ></Breadcrumbs.Item>
        )}
      </Breadcrumbs>
    );
  }

  const isDisabled = orgId => {
    return user.role !== "Global Admin" && !user.orgIds.includes(orgId);
  };

  const handleRadioChange = e => {
    setLoginType(e.target.value);
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {id ? (
                  <h1>Edit Organization: {orgData.name}</h1>
                ) : (
                  <h1>Add Organization</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Organization Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      id: orgData.id,
                      orgName: orgData.name,
                      createdBy: orgData.created_by,
                      updatedBy: orgData.updated_by,
                      shortCode: orgData.short_code,
                      loginType: orgData.login_type
                    }}
                    validationSchema={Yup.object().shape({
                      orgName: Yup.string()
                        .trim()
                        .required("Name is required.")
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      const data = {
                        name: values.orgName,
                        use_mrn_as_source_entity_id: useMrnAsSourceEntityIdMode,
                        updated_by: user.username,
                        created_by: values.createdBy,
                        short_code: values.shortCode,
                        login_type: loginType
                      };
                      let method = "";
                      let url = "";

                      if (id) {
                        data["id"] = id;

                        method = "put";
                        url = `${orgApiUrl}/${id}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${orgApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {id ? (
                              <>
                                <h4>Organization ID</h4>
                                <span>{orgData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.orgName ? errors.orgName : null
                              }
                              id="orgName"
                              label={
                                <Label
                                  error={touched.orgName && errors.orgName}
                                  htmlFor="orgName"
                                >
                                  <b>Organization Name</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.orgName}
                              width="33%"
                            />
                          </div>
                          <div>
                            <h4>Short Code </h4>
                            <div>
                              {id ? (
                                orgData.short_code
                              ) : (
                                <div>
                                  <TextBox
                                    errorText={
                                      touched.shortCode
                                        ? errors.shortCode
                                        : null
                                    }
                                    id="shortCode"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    required
                                    value={values.shortCode}
                                    width="33%"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4>Login Mode </h4>
                            <div>
                              {id ? (
                                orgData.login_type
                              ) : (
                                <div>
                                  <label className={styles.radioLabel}>
                                    <input
                                      type="radio"
                                      value="MS-Email"
                                      name="loginType"
                                      defaultChecked
                                      onChange={handleRadioChange}
                                    />
                                    MS-Email
                                  </label>
                                  <label className={styles.radioLabel}>
                                    <input
                                      type="radio"
                                      value="OHCID"
                                      name="loginType"
                                      onChange={handleRadioChange}
                                    />
                                    OHCID
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4>
                              Use MRN as Source Entity ID{" "}
                              <Tooltip>
                                <Content>
                                  <p>
                                    When enabled, HCM will use MRN from the
                                    source as Source Entity ID.
                                  </p>
                                </Content>
                              </Tooltip>
                            </h4>
                            <div>
                              <CheckBox
                                label={
                                  useMrnAsSourceEntityIdMode
                                    ? "Enabled"
                                    : "Disabled"
                                }
                                onChange={() =>
                                  setUseMrnAsSourceEntityIdMode(
                                    useMrnAsSourceEntityIdMode =>
                                      !useMrnAsSourceEntityIdMode
                                  )
                                }
                                selected={useMrnAsSourceEntityIdMode}
                              />
                            </div>
                          </div>
                          {id ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{orgData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(orgData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{orgData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(orgData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}
                          <ButtonList>
                            <Button
                              primary
                              type="submit"
                              disabled={isDisabled(orgData.id)}
                            >
                              {id ? `Save Changes` : `Save`}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push("/admin/organizations");
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddOrganization;
